import { useEffect, useRef } from 'react';
import { getActiveWorkout, setActiveWorkout, clearActiveWorkout } from '@/lib/activeWorkoutStore';
import { apiRequest } from '@/lib/queryClient';

const encouragingMessages = [
  { title: "Let's Go! 💪", body: "Rest over - time to crush your next set!" },
  { title: "You've Got This!", body: "Break's done - show those weights who's boss!" },
  { title: "Ready to Lift! 🔥", body: "Time to get back to work - you're doing amazing!" },
  { title: "Power Up!", body: "Rest complete - let's keep that momentum going!" },
  { title: "Back to It! 💪", body: "Your muscles are ready - let's go!" },
];

function getRandomMessage() {
  return encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)];
}

export function useRestTimerCheck() {
  const hasCheckedRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  
  const playAlarmSound = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const audioContext = audioContextRef.current;
      
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const playBeep = (startTime: number, duration: number, frequency: number) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = audioContext.currentTime;
      playBeep(now, 0.12, 880);
      playBeep(now + 0.15, 0.12, 988);
      playBeep(now + 0.3, 0.18, 1047);
    } catch (e) {
      console.error("Could not play alarm sound:", e);
    }
  };
  
  const showNotification = (returnUrl: string) => {
    if (!('Notification' in window)) return;
    
    if (Notification.permission === 'granted') {
      const message = getRandomMessage();
      new Notification(message.title, {
        body: message.body,
        icon: '/icons/icon-96.png',
        tag: 'rest-timer',
        data: { url: returnUrl }
      });
    }
  };
  
  const checkExpiredTimer = () => {
    const activeWorkout = getActiveWorkout();
    if (!activeWorkout) return false;
    
    const { restTimerStartedAt, restTimerDuration, restTimerExerciseId } = activeWorkout;
    
    if (!restTimerStartedAt || !restTimerDuration || !restTimerExerciseId) return false;
    
    const now = Date.now();
    const elapsed = Math.floor((now - restTimerStartedAt) / 1000);
    const remaining = restTimerDuration - elapsed;
    
    if (remaining <= 0) {
      setActiveWorkout({
        ...activeWorkout,
        restTimerExerciseId: null,
        restTimerStartedAt: null,
        restTimerDuration: null
      });
      
      const returnUrl = `/program/${activeWorkout.programId}?week=${activeWorkout.week}&workout=${activeWorkout.workoutIndex}`;
      
      showNotification(returnUrl);
      playAlarmSound();
      
      apiRequest("POST", "/api/push/cancel-rest-timer", {}).catch(() => {});
      
      return true;
    }
    
    return false;
  };
  
  useEffect(() => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;
    
    checkExpiredTimer();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkExpiredTimer();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  
  useEffect(() => {
    const interval = setInterval(() => {
      const activeWorkout = getActiveWorkout();
      if (activeWorkout?.restTimerStartedAt && activeWorkout?.restTimerDuration) {
        checkExpiredTimer();
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
}
